import { useState } from "react";
import { Box } from "@mui/material";
import { CssBaseline, ThemeProvider } from "@mui/material";

import { BarChart3 } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { DialogsProvider } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { bpmV2Theme } from "@themes/Bpm";
import { AppButton } from "@components/v2/AppButton";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";
import { setFullSearchText, setFullSearching } from "@store/slices/sessionSlice";
import { TopbarTitle } from "@components/v2/TopbarTitle";
import { TopbarProfile } from "@components/v2/TopbarProfile";
import { SearchField } from "@components/v2/SearchField";
import { NotificationsBell } from "@components/Notifications/NotificationsBell";
import { useGetCompanyQuery } from "@services/api/companies";

/**
 * Lightweight shell for the v2 Solicitudes pages. Intentionally does NOT reuse
 * `SolicitudesLayout.tsx` (toolpad `DashboardLayout` + legacy per-company theme)
 * — that layout renders its own sidebar/header, which would double up
 * navigation and clash with `bpmV2Theme`. This layout only wraps `<Outlet/>`
 * with the v2 theme and a minimal top bar, keeping the session-expiry guard
 * behavior identical to the legacy layout.
 */
export const SolicitudesV2Layout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userSession = useSelector((state: RootState) => state.session.user);
    const permissions = useSelector((state: RootState) => state.session.permissions);

    useTokenExpirationGuard();

    // Logo de la compañía principal del usuario (bandera `principal` en `session.companies`), aunque tenga varias asignadas.
    const principalCompanyId = useSelector(
        (state: RootState) => state.session.companies?.find((company) => company.principal === true)?.id,
    );
    const { data: principalCompany, isLoading: isLoadingCompany } = useGetCompanyQuery(principalCompanyId!, {
        skip: !principalCompanyId,
    });
    const principalLogo = principalCompany?.palette?.interface?.navbar?.logo;
    const logoUrl = principalLogo ? `${import.meta.env.VITE_URL_STATIC_RESOURCES}/${principalLogo}` : null;

    const [search, setSearch] = useState("");

    const initials =
        `${userSession.name?.charAt(0) ?? ""}${userSession.last_name?.charAt(0) ?? ""}`.toUpperCase() || "??";

    const handleSearchSubmit = () => {
        dispatch(setFullSearchText(search));
        dispatch(setFullSearching(true));
    };

    const handleSearchClear = () => {
        setSearch("");
        dispatch(setFullSearchText(""));
        dispatch(setFullSearching(true));
    };

    return (
        <ThemeProvider theme={bpmV2Theme}>
            <CssBaseline />
            <DialogsProvider>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100vh",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                            flexShrink: 0,
                            px: 3,
                            py: 1.5,
                            backgroundColor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <TopbarTitle ident="BPM" title="Solicitudes" logoUrl={logoUrl} logoLoading={isLoadingCompany} />
                        <SearchField
                            placeholder="Buscar por ID, RFC, Razón Social o Usuario…"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") handleSearchSubmit();
                            }}
                            onClear={handleSearchClear}
                            sx={{ flex: 1, maxWidth: 560 }}
                        />
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
                            {permissions.find((p) => p.module.code === "MREPORT")?.can_watch === true && (
                                <AppButton
                                    tone="ghost"
                                    startIcon={<BarChart3 size={14} />}
                                    sx={{ height: 34, fontSize: "0.75rem" }}
                                    onClick={() => navigate("/v2/reportes")}
                                >
                                    Reportes
                                </AppButton>
                            )}
                            {userSession.preferences.options?.showNotifications === true && <NotificationsBell />}
                            <TopbarProfile initials={initials} withAccountMenu />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                        <Outlet />
                    </Box>
                </Box>
            </DialogsProvider>
        </ThemeProvider>
    );
};
